/*
 * ZIP wizard input field
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component ( {
    selector: 'reporter-zip',
    templateUrl: './zip.component.html',
    styleUrls: ['./zip.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ZipComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ZipComponent),
            multi: true
        },
    ],
    standalone: false
} )

export class ZipComponent implements ControlValueAccessor, Validator, AfterViewInit {

  @Input () placeholder: string;

  constructor( private renderer: Renderer2, private elementRef: ElementRef ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return `${ this.innerValue }`
      .toUpperCase ()
      .replace ( /\W|[_]/g, '' );
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  isUsa = true;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit() {
    this.initialValue = this.value;
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;
  }

  writeValue( value: any ): void {
    let newVal = value
      ? ` ${ value }`
        .toUpperCase ()
        .replace ( /^\s*[ZW]+/, '' )
        .replace ( /\W|[_]/g, '' )
        .replace ( /^([DFIOQU])/, '' )
      : '';

    if ( newVal.length === 0 ) {
      newVal = '';
    }

    if ( newVal.match ( /^[A-Z]{1}/ ) !== null ) {
      // Canadian ZIP, must be A1A 1A1
      this.isUsa = false;
      const valArray = Array.from ( newVal )
        .reduce ( ( acc, v, i ) => {
          if ( acc.length === 6 ) {
            return acc;
          }
          if ( acc.length === 0 && v.match ( /[DFIOQU]/ ) !== null ) {
            return acc;
          }
          const letter = acc.length % 2 === 0;
          const spacer = acc.length === 3 ? ' ' : '';
          if ( letter && v.match ( /[A-Z]/ ) !== null ) {
            acc.push ( v );
          }
          if ( !letter && v.match ( /\d/ ) !== null ) {
            acc.push ( spacer + v );
          }
          return acc;
        }, [] );
      newVal = valArray.join ( '' );
    } else {
      // USA zip code
      this.isUsa = true;
      newVal = ` ${ value }`.replace ( /\D/g, '' );
    }

    this.innerValue = newVal;
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', newVal );
    this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );

    this.onChange ( newVal );
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( !control.value && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    if ( control.value
      && control.value !== this.initialValue
      && !/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z] [0-9][ABCEGHJ-NPRSTV-Z][0-9]$/.test ( control.value )
      && !/\d{5}/.test ( control.value ) ) {
      return {
        patternMismatch: true,
        message: ' must be a valid US or Canada ZIP.'
      };
    }
    return null;
  }
}
